// -----------------------------
// Zoom + pan state (CSS transform)

import { HEIGHT, MAX_SCALE, MIN_SCALE, WIDTH } from "../constants";

// -----------------------------
export type PanZoomApi = {
	disable(): void;
	enable(): void;
	zoomTo(params: { x: number; y: number; scale: number }): void;
};

export function usePanZoom({
	wrapperEl,
	transformEl,
	onChange,
	initialScale = 1,
	initialX = 0,
	initialY = 0,
}: {
	wrapperEl: HTMLDivElement;
	transformEl: HTMLDivElement;
	onChange?: (x: number, y: number, scale: number) => void;
	initialScale?: number;
	initialX?: number;
	initialY?: number;
}): PanZoomApi {
	const minScale = MIN_SCALE;
	const maxScale = MAX_SCALE;
	let scale = initialScale;
	let translateX = initialX;
	let translateY = initialY;

	type Pt = { x: number; y: number };
	const activePointers = new Map<number, Pt>();

	let lastCenter: Pt | null = null;
	let lastDistance = 0;
	let enabled = false;

	function doEnable() {
		enabled = true;
	}

	function doDisable() {
		enabled = false;
		activePointers.clear();
	}

	function clamp(v: number, lo: number, hi: number) {
		return Math.min(hi, Math.max(lo, v));
	}

	// Clamp so you never see empty space outside the board.
	// If the board is smaller than the viewport in a dimension, we center it.
	function clampPan() {
		if (!wrapperEl) {
			throw new Error("missing wrapper");
		}

		const viewportW = wrapperEl.clientWidth;
		const viewportH = wrapperEl.clientHeight;

		const scaledW = WIDTH * scale;
		const scaledH = HEIGHT * scale;

		let minX: number, maxX: number;
		if (scaledW <= viewportW) {
			// center horizontally
			minX = maxX = (viewportW - scaledW) / 2;
		} else {
			minX = viewportW - scaledW;
			maxX = 0;
		}

		let minY: number, maxY: number;
		if (scaledH <= viewportH) {
			// center vertically
			minY = maxY = (viewportH - scaledH) / 2;
		} else {
			minY = viewportH - scaledH;
			maxY = 0;
		}

		translateX = clamp(translateX, minX, maxX);
		translateY = clamp(translateY, minY, maxY);
		onChange?.(translateX, translateY, scale);
	}

	// keep (cx, cy) fixed in screen coords while scaling.
	function zoomByRatio(cx: number, cy: number, ratio: number) {
		if (!Number.isFinite(ratio) || ratio === 1) return;

		const nextScale = clamp(scale * ratio, minScale, maxScale);
		const appliedRatio = nextScale / scale;
		if (appliedRatio === 1) return;

		translateX = cx - appliedRatio * (cx - translateX);
		translateY = cy - appliedRatio * (cy - translateY);
		scale = nextScale;
		onChange?.(translateX, translateY, scale);
	}

	function applyTransform() {
		if (!transformEl) {
			throw new Error("missing transformEl");
		}

		clampPan();
		transformEl.style.transform = `translate(${translateX}px, ${translateY}px) scale(${scale})`;
		transformEl.style.transformOrigin = "0 0";
	}

	function distance(a: Pt, b: Pt) {
		return Math.hypot(a.x - b.x, a.y - b.y);
	}

	function centerOf(a: Pt, b: Pt): Pt {
		return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
	}

	function getLocalPoint(client: Pt): Pt {
		if (!wrapperEl) {
			throw new Error("missing wrapperEl");
		}

		const rect = wrapperEl.getBoundingClientRect();
		return { x: client.x - rect.left, y: client.y - rect.top };
	}

	function beginTwoFingerGesture() {
		// tmpDisableFabric('two-finger-start')

		const [p1, p2] = [...activePointers.values()];
		const centerClient = centerOf(p1, p2);

		lastCenter = getLocalPoint(centerClient);
		lastDistance = distance(p1, p2);

		applyTransform();
	}

	function updateOneFingerGesture() {
		const [center] = [...activePointers.values()];
		if (lastCenter) {
			translateX += center.x - lastCenter.x;
			translateY += center.y - lastCenter.y;
		}
		lastCenter = center;
		applyTransform();
	}

	function updateTwoFingerGesture() {
		const [p1, p2] = [...activePointers.values()];
		const centerClient = centerOf(p1, p2);
		const center = getLocalPoint(centerClient);
		const d = distance(p1, p2);

		// 1) PAN incrementally based on center delta (feels “native”)
		if (lastCenter) {
			translateX += center.x - lastCenter.x;
			translateY += center.y - lastCenter.y;
		}

		// 2) ZOOM incrementally around current center
		if (lastDistance > 0) {
			const ratio = d / lastDistance;
			zoomByRatio(center.x, center.y, ratio);
		}

		lastCenter = center;
		lastDistance = d;

		applyTransform();
	}

	function endTwoFingerGesture() {
		lastCenter = null;
		lastDistance = 0;
	}

	function handlePointerEnd(e: PointerEvent) {
		activePointers.delete(e.pointerId);

		if (activePointers.size < 2) {
			endTwoFingerGesture();
		} else {
			// Still 2 pointers? (rare but possible if a third finger was involved)
			beginTwoFingerGesture();
		}
	}

	wrapperEl.addEventListener("pointerdown", (e) => {
		if (!enabled) {
			return;
		}

		activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

		// Capture prevents Safari/iOS from doing weird target switching mid-gesture
		try {
			wrapperEl?.setPointerCapture(e.pointerId);
		} catch {}

		if (activePointers.size === 2) {
			e.preventDefault();
			beginTwoFingerGesture();
		}
	});

	wrapperEl.addEventListener("pointermove", (e) => {
		if (!enabled) {
			return;
		}

		if (!activePointers.has(e.pointerId)) return;
		activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

		if (activePointers.size === 2) {
			e.preventDefault();
			// tmpDisableFabric('two-finger-move')
			updateTwoFingerGesture();
		} else if (activePointers.size === 1) {
			updateOneFingerGesture();
		}
	});

	wrapperEl.addEventListener("pointerup", (e) => {
		if (!enabled) {
			return;
		}
		handlePointerEnd(e);
	});

	wrapperEl.addEventListener("pointercancel", () => {
		if (!enabled) {
			return;
		}
		activePointers.clear();
		endTwoFingerGesture();
	});

	applyTransform();

	return {
		enable: doEnable,
		disable: doDisable,
		zoomTo: (params: { x: number; y: number; scale: number }) => {
			translateX = params.x;
			translateY = params.y;
			scale = params.scale;
			applyTransform();
		},
	};
}
